/**
 * Composable for Reverse IP Lookup functionality.
 */
import { ref, reactive } from 'vue';
import type { ApiResponse, ReverseIpResult, Domain } from '../types';

export function useReverseIpLookup() {
  const loading = ref(false);
  const error = ref<string | null>(null);
  const result = ref<ReverseIpResult | null>(null);

  const config = reactive({
    max_results: 2000,
  });

  /**
   * Perform reverse IP lookup.
   */
  const lookup = async (ipAddress: string) => {
    loading.value = true;
    error.value = null;
    result.value = null;

    try {
      const response = await fetch('/wp-json/conflated-domains/v1/tools/reverse-ip', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ip_address: ipAddress,
          max_results: config.max_results,
        }),
      });

      const data: ApiResponse = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Lookup failed');
      }

      if (data.success && data.data) {
        result.value = data.data;
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'An error occurred';
    } finally {
      loading.value = false;
    }
  };

  /**
   * Export results as CSV.
   */
  const exportCSV = (resultsToExport?: Domain[]) => {
    const domains = resultsToExport || result.value?.domains;

    if (!domains || domains.length === 0) {
      alert('No results to export');
      return;
    }

    const headers = ['Domain', 'Source', 'Status', 'SSL', 'Last Seen'];
    const rows = domains.map((d) => [
      d.domain,
      d.source,
      d.status,
      d.ssl ? 'Yes' : 'No',
      d.last_seen || 'N/A',
    ]);

    const csv = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    downloadFile(csv, 'reverse-ip-results.csv', 'text/csv');
  };

  /**
   * Export results as JSON.
   */
  const exportJSON = (resultsToExport?: Domain[]) => {
    const domains = resultsToExport || result.value?.domains;

    if (!domains || domains.length === 0) {
      alert('No results to export');
      return;
    }

    const json = JSON.stringify(domains, null, 2);
    downloadFile(json, 'reverse-ip-results.json', 'application/json');
  };

  /**
   * Download file helper.
   */
  const downloadFile = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return {
    loading,
    error,
    result,
    config,
    lookup,
    exportCSV,
    exportJSON,
  };
}
